import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, EmptyRow, FilterBar, PageHeader, Pager, SelectField, SortHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { timeZoneNames } from '../sites/api';
import { BASE, BuildingArray, SrBuilding, keys } from './api';
import type { SrBuildingCreate } from './api';

const PAGE_SIZE = 20;

function CreateBuilding({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', address: '', timeZone: 'Europe/London', operatorName: '', operatorContactEmail: '', alertEmail: '' });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const create = useMutation({
    mutationFn: () => {
      const body: SrBuildingCreate = { ...form };
      return api.post(BASE, { body, schema: SrBuilding });
    },
    onSuccess: async (b) => {
      await qc.invalidateQueries({ queryKey: keys.buildings });
      toast(t('srp.create.done'));
      onDone();
      void navigate(`/platform/shared-reception/${b.id}`);
    },
  });
  const errs = fieldErrors(create.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Card title={t('srp.create.title')} description={t('srp.create.description')} className="mb-6">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <TextField label={t('srp.field.name')} value={form.name} onChange={set('name')} error={errs['name']} required />
        <TextField label={t('srp.field.address')} value={form.address} onChange={set('address')} error={errs['address']} required />
        <SelectField label={t('srp.field.timeZone')} hint={t('srp.field.timeZoneHint')} value={form.timeZone} onChange={set('timeZone')} error={errs['timeZone']}>
          {timeZoneNames(form.timeZone).map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </SelectField>
        <TextField label={t('srp.field.operatorName')} hint={t('srp.field.operatorNameHint')} value={form.operatorName} onChange={set('operatorName')} error={errs['operatorName']} required />
        <TextField label={t('srp.field.operatorEmail')} type="email" value={form.operatorContactEmail} onChange={set('operatorContactEmail')} error={errs['operatorContactEmail']} required />
        <TextField label={t('srp.field.alertEmail')} hint={t('srp.field.alertEmailHint')} type="email" value={form.alertEmail} onChange={set('alertEmail')} error={errs['alertEmail']} />
        <div className="flex flex-wrap items-center justify-end gap-2 sm:col-span-2">
          <ErrorLine error={Object.keys(errs).length ? null : create.error} />
          <Button onClick={onDone}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {t('srp.create.submit')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Platform: every shared reception (building). The server returns the whole list, so search, sort and paging happen here. */
export function BuildingsPage() {
  const list = useListState({ sort: 'name' });
  const canManage = useCan('platform.shared_reception_manage');
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState('');
  const q = useQuery({ queryKey: keys.buildings, queryFn: () => api.get(BASE, { schema: BuildingArray }) });

  const rows = useMemo(() => {
    const needle = list.search.trim().toLowerCase();
    const filtered = (q.data ?? []).filter(
      (b) => (!status || b.status === status) && (!needle || `${b.name} ${b.address} ${b.operatorName}`.toLowerCase().includes(needle)),
    );
    const dir = list.order === 'asc' ? 1 : -1;
    const value = (b: SrBuilding): string | number =>
      list.sort === 'companies' ? b.companies : list.sort === 'devices' ? b.devices : list.sort === 'createdAt' ? b.createdAt : list.sort === 'status' ? b.status : b.name.toLowerCase();
    return [...filtered].sort((a, b) => (value(a) < value(b) ? -dir : value(a) > value(b) ? dir : 0));
  }, [q.data, list.search, list.sort, list.order, status]);
  const shown = rows.slice((list.page - 1) * PAGE_SIZE, list.page * PAGE_SIZE);
  const sortProps = { sort: list.sort, order: list.order, onSort: list.toggleSort };

  return (
    <>
      <PageHeader
        title={t('srp.nav.buildings')}
        description={t('srp.nav.buildings.desc')}
        actions={
          canManage ? (
            <Button variant="primary" disabled={creating} onClick={() => setCreating(true)}>
              {t('srp.list.new')}
            </Button>
          ) : null
        }
      />
      {creating ? <CreateBuilding onDone={() => setCreating(false)} /> : null}
      <FilterBar columns="sm:grid-cols-2">
        <TextField label={t('common.search')} hint={t('srp.list.searchHint')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('common.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('common.all')}</option>
          <option value="active">{t('srp.status.active')}</option>
          <option value="archived">{t('srp.status.archived')}</option>
        </SelectField>
      </FilterBar>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('srp.list.table')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <SortHeader label={t('srp.col.name')} field="name" {...sortProps} />
                <Th>{t('srp.col.address')}</Th>
                <Th>{t('srp.col.operator')}</Th>
                <SortHeader label={t('srp.col.companies')} field="companies" {...sortProps} />
                <SortHeader label={t('srp.col.devices')} field="devices" {...sortProps} />
                <SortHeader label={t('srp.col.status')} field="status" {...sortProps} />
                <SortHeader label={t('srp.col.created')} field="createdAt" {...sortProps} />
                <Th>{t('srp.col.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? <EmptyRow cols={8} /> : null}
              {shown.map((b) => (
                <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{b.name}</td>
                  <td className="px-4 py-3">{b.address}</td>
                  <td className="px-4 py-3">{b.operatorName}</td>
                  <td className="px-4 py-3">{b.companies}</td>
                  <td className="px-4 py-3">{b.devices}</td>
                  <td className="px-4 py-3">
                    <Badge tone={b.status === 'active' ? 'good' : 'neutral'}>{t(`srp.status.${b.status}`)}</Badge>
                  </td>
                  <td className="px-4 py-3">{formatDateTime(b.createdAt, b.timeZone)}</td>
                  <td className="px-4 py-3">
                    <Link className="font-medium text-brand hover:underline" to={`/platform/shared-reception/${b.id}`} aria-label={t('srp.openNamed', { name: b.name })}>
                      {t('srp.open')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={list.page} pageSize={PAGE_SIZE} total={rows.length} onPage={list.setPage} />
        </>
      )}
    </>
  );
}
